import { ErrorMessage, TaAutocomplete } from '@components/shared';
import { Dictionary } from '@utils/util.types';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export const AutocompleteField = ({
	formik,
	name,
	label,
	options,
	icon,
	multiple = false,
	defaultOption,
}: {
	formik: any;
	name: string;
	label: string;
	options: Dictionary[];
	icon: JSX.Element;
	multiple?: boolean;
	defaultOption?: Dictionary;
}) => {
	const { t } = useTranslation();
	const [value, setValue] = useState(formik.values[name]);
	const hasSetDefaultRef = useRef(false);
	const previousValueRef = useRef(formik.values[name]);

	// Set default value on initial render if needed - ONLY ONCE
	useEffect(() => {
		// Only run this once
		if (hasSetDefaultRef.current) {
			return;
		}
		
		if (
			(formik.values[name] === undefined || 
			(typeof formik.values[name] === 'string' && formik.values[name] === 'undefined') || 
			(typeof formik.values[name] === 'object' && formik.values[name]?.name === 'undefined')) && 
			defaultOption && 
			name === 'crop_unit'
		) {
			console.log(`AutocompleteField: Setting default option for ${name}:`, defaultOption);
			setValue(defaultOption);
			formik.setFieldValue(name, defaultOption);
		}
		
		hasSetDefaultRef.current = true;
	}, [defaultOption, formik, name]);

	// Handle value changes from formik - with safeguards against loops
	useEffect(() => {
		const currentValue = formik.values[name];
		
		// Skip if the value hasn't actually changed
		if (JSON.stringify(currentValue) === JSON.stringify(previousValueRef.current)) {
			return;
		}
		
		// Update our ref with the new value
		previousValueRef.current = currentValue;
		
		// Skip if value is undefined and we have a default option
		if (
			(currentValue === undefined || 
			(typeof currentValue === 'string' && currentValue === 'undefined') || 
			(typeof currentValue === 'object' && currentValue?.name === 'undefined')) && 
			defaultOption && 
			name === 'crop_unit'
		) {
			return;
		}
		
		// Handle string values (convert to object)
		if (typeof currentValue === 'string' && options.length > 0) {
			console.log(`AutocompleteField: ${name} has string value:`, currentValue);
			
			// Try to find by id or name
			const matchingOption = options.find(
				option => option.id === currentValue || option.name === currentValue
			);
			
			if (matchingOption) {
				console.log(`AutocompleteField: Found matching option for ${name}:`, matchingOption);
				setValue(matchingOption);
			}
		} else if (currentValue !== undefined) {
			setValue(currentValue);
		}
	}, [formik.values[name], name, options, defaultOption]);

	return (
		<div>
			<TaAutocomplete
				filterSelectedOptions
				required
				options={options}
				getOptionLabel={(option) => option.name || ''}
				label={label}
				icon={icon}
				multiple={multiple}
				value={value}
				onChange={(_, newValue) => {
					setValue(newValue);
					formik.setFieldValue(name, newValue);
				}}
				error={Boolean(formik.touched[name] && formik.errors[name])}
			/>
			{formik.touched[name] && formik.errors[name] && (
				<ErrorMessage message={t(formik.errors[name] as string, { fieldName: label })} />
			)}
		</div>
	);
};
